import { ActionButton } from '@/common/buttons/ActionButton';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

// Labels a Price Result's earned period as a quarter (2026Q1) or month (2026M01) when the dates line up with one,
// otherwise falls back to the raw date range
const periodLabel = (startDate, endDate) => {
  const s = String(startDate);
  const period = [DateUtils.qtrPeriodFromDate(startDate), `${s.slice(0, 4)}M${s.slice(4, 6)}`].find(
    (candidate) => DateUtils.periodStartDate(candidate) === startDate && DateUtils.periodEndDate(candidate) === endDate,
  );
  return period ?? `${DateUtils.format(startDate)} - ${DateUtils.format(endDate)}`;
};

const periodKey = (row) => `${row.startDate}-${row.endDate}`;

const tierColDef = (field, displayName, idField) => ({
  field,
  displayName,
  idField,
  type: 'PRICE_RES_TIER',
  filter: 'agNumberColumnFilter',
  module: 'md',
  entity: 'PROG_PROD_TIER',
  decimalPrecision: 0,
  minDecimalPrecision: 0,
});

export const PriceResList = ({ progObj }) => {
  const rsrc = useRsrc();
  const tierBased = Boolean(progObj?.tierBased);

  const [periods, setPeriods] = useState([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState('');
  const [periodMenuAnchorEl, setPeriodMenuAnchorEl] = useState(null);
  const [tierIdToNum, setTierIdToNum] = useState(tierBased ? null : {});
  const [selectedPriceRess, setSelectedPriceRess] = useState([]);

  useEffect(() => {
    if (!tierBased) return;
    rsrc
      .rows('md', 'PROG_PROD_TIER', { query: 'TIER_LIST', expr: ExprUtils.whereParams('progId', progObj.id) })
      .then((tiers) => setTierIdToNum(Object.fromEntries((tiers ?? []).map((tier) => [tier.id, tier.tier]))));
  }, [tierBased, progObj.id]);

  const clientSideRowData = useCallback(
    (setDataSource) => {
      rsrc.rows('md', 'PRICE_RES', { expr: ExprUtils.whereParams('progId', progObj.id) }).then((rows = []) => {
        const today = Number(DateUtils.today());
        const keyToPeriod = {};
        for (const row of rows) {
          keyToPeriod[periodKey(row)] ??= {
            key: periodKey(row),
            startDate: row.startDate,
            endDate: row.endDate,
            label: periodLabel(row.startDate, row.endDate),
            current: today >= row.startDate && today <= row.endDate,
          };
        }
        setPeriods(Object.values(keyToPeriod).sort((a, b) => b.startDate - a.startDate || b.endDate - a.endDate));

        setDataSource(
          rows
            .filter((row) => !selectedPeriodKey || periodKey(row) === selectedPeriodKey)
            .map((row) => ({
              ...row,
              tierNum: tierIdToNum[row.tierId] ?? null,
              ovrdTierNum: tierIdToNum[row.ovrdTierId] ?? null,
            })),
        );
      });
    },
    [progObj.id, selectedPeriodKey, tierIdToNum],
  );

  const augFieldDefs = useCallback(
    (fieldToFieldDef) => {
      fieldToFieldDef.conc.type = 'PRICE_RES_CONC';
      delete fieldToFieldDef.tierVars;
      delete fieldToFieldDef.tierId;
      delete fieldToFieldDef.ovrdTierId;

      if (!tierBased) {
        delete fieldToFieldDef.tierFormula;
        return;
      }

      fieldToFieldDef.checkbox = GridConstants.checkbox;
      fieldToFieldDef.tierFormula.displayName = 'Formula';
      fieldToFieldDef.tierFormula.type = 'PRICE_RES_FORMULA';
      fieldToFieldDef.tierFormula.minWidth = 600;
      fieldToFieldDef.tierNum = tierColDef('tierNum', 'Tier', 'tierId');
      fieldToFieldDef.ovrdTierNum = tierColDef('ovrdTierNum', 'Override Tier', 'ovrdTierId');
    },
    [tierBased],
  );

  const adjTierData = (incDec) => ({
    title: incDec === 'INC' ? 'Increase Tier' : 'Decrease Tier',
    entity: 'PRICE_RES',
    pluginCode: 'ADJ_TIER_ACTION_PLUGIN',
    addlReqData: { progId: progObj.id, selectedEntities: selectedPriceRess, incDec },
  });

  const selectedPeriod = periods.find((period) => period.key === selectedPeriodKey);

  const augTabButtons = (tabButtons) => {
    ObjUtils.clearObj(tabButtons);
    tabButtons.push(
      <Button
        key="period"
        size="small"
        startIcon={<FontAwesomeIcon icon={fas.faCalendar} />}
        endIcon={<FontAwesomeIcon icon={fas.faCaretDown} />}
        onClick={(event) => setPeriodMenuAnchorEl(event.currentTarget)}
      >
        Earned Period: {selectedPeriod?.label ?? 'All'}
      </Button>,
    );
    if (tierBased) {
      tabButtons.push(
        <ActionButton
          key="incTier"
          data={adjTierData('INC')}
          toolTip="Increase Tier"
          icon={fas.faPlus}
          disabled={!selectedPriceRess.length}
        />,
        <ActionButton
          key="decTier"
          data={adjTierData('DEC')}
          toolTip="Decrease Tier"
          icon={fas.faMinus}
          disabled={!selectedPriceRess.length}
        />,
      );
    }
  };

  const periodMenuItem = (key, label) => (
    <MenuItem
      key={key || '__all'}
      selected={selectedPeriodKey === key}
      onClick={() => {
        setSelectedPeriodKey(key);
        setPeriodMenuAnchorEl(null);
      }}
    >
      <ListItemIcon>{selectedPeriodKey === key && <FontAwesomeIcon icon={fas.faCheck} />}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </MenuItem>
  );

  if (tierIdToNum == null) return null;

  return (
    <>
      <EntityList
        entity="PRICE_RES"
        clientSideRowData={clientSideRowData}
        augTabButtons={augTabButtons}
        augFieldDefs={augFieldDefs}
        gridOptions={tierBased ? { rowSelection: 'multiple' } : undefined}
        events={
          tierBased ?
            {
              onSelectionChanged: (event) =>
                setSelectedPriceRess(event.api.getSelectedNodes().map((node) => node.data)),
            }
          : undefined
        }
      />
      <Menu
        anchorEl={periodMenuAnchorEl}
        open={!!periodMenuAnchorEl}
        onClose={() => setPeriodMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {periodMenuItem('', 'All')}
        {periods.map((period) => periodMenuItem(period.key, `${period.label}${period.current ? ' (Current)' : ''}`))}
      </Menu>
    </>
  );
};
